/* eslint-disable no-useless-constructor */
/* eslint-disable max-len */
/* eslint-disable no-mixed-spaces-and-tabs */
// eslint-disable-next-line no-unused-vars
import { SelfieSegmentation } from '@mediapipe/selfie_segmentation';
import { store } from './store/store';

const VB = '[VB matting]';

export default class ChromaKey
{
	capStream = null;
	paused = false;
	selfieSegmentation = null;
	backgroundImg = null;
	inputStream = null;
	bg = null;
	_loopHandle = null;
	_loopRunning = false;
	_loopToken = 0;
	_frameIntervalMs = 66;
	_lastSendStartedAt = 0;
	constructor()
	{
		console.log(`${VB} ChromaKey instance created`);
	}

	_updateProcessingProfile(blur)
	{
		// Keep quality acceptable while reducing CPU load.
		// Blur needs more compositing work than static image replacement.
		const configuredFps = Number(store.getState()?.settings?.frameRate) || 15;
		const maxTargetFps = (blur === 'blur') ? 12 : 15;
		const targetFps = Math.max(8, Math.min(configuredFps, maxTargetFps));

		this._frameIntervalMs = Math.round(1000 / targetFps);
		console.log(`${VB} profile`, { configuredFps, targetFps, frameIntervalMs: this._frameIntervalMs, blur });
	}

	_startProcessingLoop()
	{
		if (this._loopRunning)
		{
			return;
		}

		this._loopRunning = true;
		const token = ++this._loopToken;

		const tick = async () =>
		{
			if (!this._loopRunning || token !== this._loopToken)
			{
				return;
			}

			if (this.paused || !this.video || this.video.paused || this.video.ended)
			{
				this._loopHandle = setTimeout(tick, this._frameIntervalMs);
				return;
			}

			this._lastSendStartedAt = performance.now();
			try
			{
				await this.sendFrame();
			}
			catch (e)
			{
				console.error(`${VB} processing tick failed`, e);
				this._loopHandle = setTimeout(tick, this._frameIntervalMs);
				return;
			}

			const elapsed = performance.now() - this._lastSendStartedAt;
			const nextDelay = Math.max(0, this._frameIntervalMs - elapsed);

			this._loopHandle = setTimeout(tick, nextDelay);
		};

		tick();
	}

	_stopProcessingLoop()
	{
		this._loopRunning = false;
		this._loopToken += 1;
		if (this._loopHandle)
		{
			clearTimeout(this._loopHandle);
			this._loopHandle = null;
		}
	}

	updateStream(stream)
	{
		this.capStream = null;
		this.canvasCtx = null;
		this.video = null;
		this.canvas = null;

		this.video = document.createElement('video');
		this.video.autoplay = true;
		this.video.srcObject = stream;

		this.canvas = document.createElement('canvas');
		this.canvasCtx = this.canvas.getContext('2d');

		this.video.addEventListener('play', () =>
		{
			this.width = this.canvas.width = this.video.videoWidth;
			this.height = this.canvas.height = this.video.videoHeight;
			this.timerCallback();
		}, false);

		this.capStream = this.canvas.captureStream();
	}

	/**
	 * Change only the background image (and optionally blur type) without creating a new WebGL context.
	 * Reuses the same pipeline and track, so no black frame and no new WebGL context.
	 */
	updateBackground(bg, blur)
	{
		console.log(`${VB} updateBackground() called`, { bg, blur, hadCapStream: !!this.capStream, hadVideo: !!this.video });
		this.backgroundImg = null;
		this.paused = true;
		this.bg = bg;
		if (blur !== undefined)
		{
			this.currentBlur = blur;
		}
		else
		{
			// Callers that only pass the image (e.g. texture pickers: changeBackground(src))
			// send blur as undefined. Without clearing, a previous 'blur' / 'lightBlur' choice
			// would stick and _onResults would keep drawing the blur path instead of the image.
			this.currentBlur = null;
		}
		this._updateProcessingProfile(this.currentBlur);
		this.backgroundImg = new Image();
		const src = this.bg.startsWith('https') ? this.bg : `/images/banners/vc-banners/${this.bg}`;
		console.log(`${VB} updateBackground() loading image`, { src });
		if (this.bg.startsWith('https'))
		{
			this.backgroundImg.crossOrigin = 'anonymous';
			this.backgroundImg.alt = '';
			this.backgroundImg.src = this.bg;
		}
		else
		{
			this.backgroundImg.src = `/images/banners/vc-banners/${this.bg}`;
		}
		this.backgroundImg.onerror = (e) =>
		{
			console.error(`${VB} updateBackground() background image FAILED to load`, { src, error: e });
		};
		this.backgroundImg.onload = () =>
		{
			console.log(`${VB} updateBackground() background image loaded`, { naturalWidth: this.backgroundImg?.naturalWidth, naturalHeight: this.backgroundImg?.naturalHeight });
			this.paused = false;
			this.timerCallback();
		};
	}

	async getImageFrame()
	{
		const src = this.bg.startsWith('https') ? this.bg : `/images/banners/vc-banners/${this.bg}`;
		console.log(`${VB} getImageFrame() loading`, { bg: this.bg, src });
		this.backgroundImg = new Image();

		if (this.bg.startsWith('https'))
		{
			this.backgroundImg.crossOrigin = 'anonymous';
			this.backgroundImg.alt = '';
			this.backgroundImg.src = this.bg;
		}
		else
		{
			this.backgroundImg.src = `/images/banners/vc-banners/${this.bg}`;
		}

		this.backgroundImg.onerror = (e) =>
		{
			console.error(`${VB} getImageFrame() image FAILED to load`, { src, error: e });
		};
		this.backgroundImg.onload = () =>
		{
			console.log(`${VB} getImageFrame() image loaded`, { naturalWidth: this.backgroundImg?.naturalWidth, naturalHeight: this.backgroundImg?.naturalHeight });
			this.timerCallback();
		};
	}

	/**
	 * Returns a Promise that resolves when the first frame has been drawn to the canvas.
	 * Call this after doLoad() and await before using capStream so replaceTrack doesn't use a black frame.
	 */
	waitForFirstFrame()
	{
		const p = this._firstFrameReady || Promise.resolve();
		console.log(`${VB} waitForFirstFrame() called`, { hasPromise: !!this._firstFrameReady });
		return p;
	}

	/**
	 * Ensure video element has non-zero intrinsic dimensions before we size the
	 * canvas. Some browsers fire 'play' before videoWidth/videoHeight are set,
	 * which would make the canvas 0x0 and produce a black frame.
	 */
	async _ensureVideoDimensions(maxWaitMs = 2000)
	{
		const start = performance.now();

		while (this.video && this.video.videoWidth === 0 && this.video.videoHeight === 0 && (performance.now() - start) < maxWaitMs)
		{
			// eslint-disable-next-line no-await-in-loop
			await new Promise((r) => setTimeout(r, 50));
		}

		console.log(`${VB} _ensureVideoDimensions()`, {
			videoWidth  : this.video?.videoWidth,
			videoHeight : this.video?.videoHeight,
			waitedMs    : Math.round(performance.now() - start)
		});

		// If still 0x0, fall back to a reasonable default so we don't keep a 0x0 canvas.
		if (this.video && this.video.videoWidth === 0 && this.video.videoHeight === 0)
		{
			this.video.videoWidth = 640;
			this.video.videoHeight = 360;
			console.warn(`${VB} _ensureVideoDimensions() using fallback 640x360 because video dimensions stayed 0x0`);
		}
	}

	doLoad(stream, bg, blur)
	{
		const videoTracks = stream?.getVideoTracks?.() || [];
		console.log(`${VB} doLoad() called`, { bg, blur, streamVideoTracks: videoTracks.length, streamId: stream?.id });
		this.bg = bg;
		this.currentBlur = blur;
		this._updateProcessingProfile(this.currentBlur);
		this._firstFrameReady = new Promise((resolve) =>
		{
			this._firstFrameReadyResolve = resolve;
		});

		const isReuse = !!(this.selfieSegmentation && this.video && this.canvas);
		console.log(`${VB} doLoad() isReuse=${isReuse}`, { hasSegmenter: !!this.selfieSegmentation, hasVideo: !!this.video, hasCanvas: !!this.canvas });

		if (!isReuse)
		{
			this.video = document.createElement('video');
			this.canvas = document.createElement('canvas');
			this.canvasCtx = this.canvas.getContext('2d');
			this.capStream = this.canvas.captureStream();

			this.video.autoplay = true;
			this.video.addEventListener('play', async () =>
			{
				await this._ensureVideoDimensions();

				this.width = this.canvas.width = this.video.videoWidth;
				this.height = this.canvas.height = this.video.videoHeight;
				console.log(`${VB} doLoad() video 'play' fired`, { width: this.width, height: this.height });
				this.getImageFrame();
			}, false);

			this.selfieSegmentation = new SelfieSegmentation({
				locateFile : (file) =>
					`https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`
			});

			this.selfieSegmentation.setOptions({
				// Use the more general model and tighter confidences for better
				// masks, at the cost of a bit more compute.
				modelSelection         : 0,
				selfieMode             : false,
				effect                 : 'mask',
				smoothSegmentationMask : true,
				enableFaceGeometry     : true,
				enableSmoothing        : true,
				minDetectionConfidence : 0.7,
				minTrackingConfidence  : 0.7
			});

			this.selfieSegmentation.onResults((results) => this._onResults(results));
		}

		this.video.srcObject = stream;
		this.video.autoplay = true;
		if (isReuse)
		{
			console.log(`${VB} doLoad() reuse path: calling video.play() and getImageFrame()`);
			this.video.play().catch((e) => console.warn(`${VB} doLoad() reuse video.play() failed`, e));
			this.getImageFrame();
		}
		else
		{
			console.log(`${VB} doLoad() fresh path: calling video.play()`);
			this.video.play().catch((e) => console.warn(`${VB} doLoad() video.play() failed`, e));
		}
		console.log(`${VB} doLoad() capStream`, { hasCapStream: !!this.capStream, trackCount: this.capStream?.getVideoTracks?.()?.length ?? 0 });
	}

	_ensurePersonCanvas(width, height)
	{
		if (!this._personCanvas || this._personCanvas.width !== width || this._personCanvas.height !== height)
		{
			this._personCanvas = document.createElement('canvas');
			this._personCanvas.width = width;
			this._personCanvas.height = height;
		}

		return this._personCanvas;
	}

	/**
	 * Mask + person (segmentation) composite. Optional horizontal flip for mirror-without-flipping-background.
	 */
	_drawPersonSegment(ctx, results, w, h, mirrorX)
	{
		ctx.save();
		if (mirrorX)
		{
			ctx.translate(w, 0);
			ctx.scale(-1, 1);
		}
		ctx.imageSmoothingEnabled = false;
		ctx.imageSmoothingQuality = 'high';
		ctx.filter = 'blur(3px)';
		ctx.drawImage(results.segmentationMask, 0, 0, w, h);
		ctx.filter = 'none';
		ctx.globalCompositeOperation = 'source-in';
		ctx.imageSmoothingEnabled = false;
		ctx.imageSmoothingQuality = 'medium';
		ctx.drawImage(results.image, 0, 0, w, h);
		ctx.restore();
	}

	_onResults(results)
	{
		const blur = this.currentBlur;
		const isBlurEffect = (blur === 'blur' || blur === 'lightBlur');
		const isFirstFrame = !!this._firstFrameReadyResolve;
		if (this._firstFrameReadyResolve)
		{
			console.log(`${VB} _onResults() FIRST FRAME drawn`, { canvasW: this.canvas?.width, canvasH: this.canvas?.height, blur, backgroundImgReady: !!(this.backgroundImg && this.backgroundImg.complete && this.backgroundImg.naturalWidth) });
			this._firstFrameReadyResolve();
			this._firstFrameReadyResolve = null;
		}

		const w = this.canvas.width;
		const h = this.canvas.height;
		const bgReady = this.backgroundImg && this.backgroundImg.complete && this.backgroundImg.naturalWidth;

		if (isFirstFrame && !bgReady && !isBlurEffect)
		{
			console.warn(`${VB} _onResults() first frame but background image NOT ready`, { hasImg: !!this.backgroundImg, complete: this.backgroundImg?.complete, naturalWidth: this.backgroundImg?.naturalWidth });
		}

		if (isBlurEffect)
		{
			this.canvasCtx.clearRect(0, 0, w, h);
			this._drawPersonSegment(this.canvasCtx, results, w, h, false);
			this.canvasCtx.globalCompositeOperation = 'destination-over';
			this.canvasCtx.filter = blur === 'blur' ? 'blur(10px)' : 'blur(5px)';
			this.canvasCtx.drawImage(results.image, 0, 0, w, h);
			this.canvasCtx.filter = 'none';

			return;
		}

		if (store.getState().settings.mirrorOwnVideo)
		{
			const pCanvas = this._ensurePersonCanvas(w, h);
			const pctx = pCanvas.getContext('2d');

			pctx.clearRect(0, 0, w, h);
			this._drawPersonSegment(pctx, results, w, h, true);

			this.canvasCtx.clearRect(0, 0, w, h);
			if (bgReady)
			{
				this.canvasCtx.drawImage(this.backgroundImg, 0, 0, w, h);
				this.canvasCtx.globalCompositeOperation = 'source-over';
				this.canvasCtx.drawImage(pCanvas, 0, 0, w, h);
				this.canvasCtx.globalCompositeOperation = 'source-over';
			}
			else
			{
				this.canvasCtx.drawImage(pCanvas, 0, 0, w, h);
			}

			return;
		}

		this.canvasCtx.clearRect(0, 0, w, h);
		this._drawPersonSegment(this.canvasCtx, results, w, h, false);
		this.canvasCtx.globalCompositeOperation = 'destination-atop';
		if (bgReady)
		{
			this.canvasCtx.drawImage(this.backgroundImg, 0, 0, w, h);
		}
	}

	async timerCallback()
	{
		this._startProcessingLoop();
	}

	async sendFrame()
	{
		try
		{
			await this.selfieSegmentation.send({ image: this.video });
		}
		catch (e)
		{
			console.error(`${VB} sendFrame() failed`, e);
			throw e;
		}
	}

	close()
	{
		console.log(`${VB} close() called`);
		this._stopProcessingLoop();
		this.paused = true;
		this.bg = null;
		this.currentBlur = null;
		this.video = null;
		this.canvas = null;
		this.canvasCtx = null;
		this.capStream = null;
		this.selfieSegmentation = null;
		this.backgroundImg = null;
		this._firstFrameReady = null;
		this._firstFrameReadyResolve = null;
		this._personCanvas = null;
		this._lastSendStartedAt = 0;
	}
}